import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Student } from '../../../../utils/firebase';
import Card from '../../../../components/ui/Card';
import Button from '../../../../components/ui/Button';
import { MarbleRouletteEngine } from './marbleRoulette/MarbleRouletteEngine';
import { stages } from './marbleRoulette/data/maps';
import type { StageDef } from './marbleRoulette/data/maps';
import MarbleMapEditor from './MarbleMapEditor';
import {
  cloneStage,
  defaultWheelStage,
  loadCustomStageFromStorage,
} from './marbleRoulette/stageStorage';

interface MarbleLotteryProps {
  students: Student[];
}

const MarbleLottery: React.FC<MarbleLotteryProps> = ({ students }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [winners, setWinners] = useState<Student[]>([]);
  const [showRanking, setShowRanking] = useState(false);
  const [physicsReady, setPhysicsReady] = useState(false);
  const [physicsError, setPhysicsError] = useState<string | null>(null);

  const [mapSource, setMapSource] = useState<'builtin' | 'custom'>(() =>
    loadCustomStageFromStorage() ? 'custom' : 'builtin'
  );
  const [builtinMapIndex, setBuiltinMapIndex] = useState(0);
  const [customStage, setCustomStage] = useState<StageDef | null>(() => loadCustomStageFromStorage());
  const [speed, setSpeed] = useState(1);
  const [showMapEditor, setShowMapEditor] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<MarbleRouletteEngine | null>(null);

  const remainingStudents = useMemo(
    () => students.filter(s => !winners.some(w => w.uid === s.uid)),
    [students, winners]
  );

  const effectiveStage = useMemo((): StageDef => {
    if (mapSource === 'custom') {
      return customStage ? cloneStage(customStage) : defaultWheelStage();
    }
    return cloneStage(stages[builtinMapIndex] ?? stages[0]!);
  }, [mapSource, builtinMapIndex, customStage]);

  const onGoal = useCallback((student: Student) => {
    setIsRunning(false);
    setSelectedStudent(student);
    setWinners(prev => [...prev, student]);
    setShowRanking(true);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const engine = new MarbleRouletteEngine({
      canvas,
      onGoal,
      initialBuiltinStageIndex: builtinMapIndex,
    });
    engineRef.current = engine;

    engine
      .init()
      .then(() => {
        setPhysicsReady(true);
        setPhysicsError(null);
      })
      .catch(err => {
        setPhysicsError(err instanceof Error ? err.message : String(err));
        setPhysicsReady(false);
      });

    return () => {
      engine.destroy();
      engineRef.current = null;
      setPhysicsReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 엔진은 마운트 시 1회만 생성
  }, [onGoal]);

  useEffect(() => {
    const eng = engineRef.current;
    if (!eng?.isReady) return;
    if (mapSource === 'custom') {
      eng.setStage(customStage ? cloneStage(customStage) : defaultWheelStage());
    } else {
      eng.setBuiltinStageIndex(builtinMapIndex);
    }
  }, [physicsReady, mapSource, builtinMapIndex, customStage]);

  useEffect(() => {
    if (engineRef.current?.isReady) {
      engineRef.current.setSpeed(speed);
    }
  }, [physicsReady, speed]);

  const startRound = () => {
    if (!engineRef.current?.isReady || remainingStudents.length === 0 || isRunning) return;

    if (remainingStudents.length === 1) {
      const only = remainingStudents[0]!;
      setSelectedStudent(only);
      setWinners(prev => [...prev, only]);
      setShowRanking(true);
      return;
    }

    setIsRunning(true);
    setSelectedStudent(null);
    engineRef.current.setParticipants(remainingStudents);
    engineRef.current.start();
  };

  const drawNext = () => {
    if (winners.length >= students.length || isRunning) return;
    setSelectedStudent(null);
    startRound();
  };

  const reset = () => {
    setWinners([]);
    setSelectedStudent(null);
    setShowRanking(false);
    setIsRunning(false);
    if (engineRef.current?.isReady) {
      engineRef.current.resetRound();
    }
  };

  const handleEditorSave = (stage: StageDef) => {
    setCustomStage(cloneStage(stage));
    setMapSource('custom');
    setShowMapEditor(false);
  };

  const canStart =
    physicsReady &&
    students.length > 0 &&
    winners.length < students.length &&
    !isRunning &&
    remainingStudents.length > 0;

  return (
    <Card style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2
          style={{
            fontSize: '1.25rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
            color: 'var(--Color-text-normal, #161616)',
          }}
        >
          구슬 뽑기
        </h2>
        <p
          style={{
            fontSize: '0.875rem',
            color: 'var(--Color-text-normal, #666)',
            marginBottom: '1rem',
          }}
        >
          구슬이 트랙을 굴러 내려와 가장 먼저 결승선을 통과한 학생이 당첨됩니다.{' '}
          <a href="https://lazygyu.github.io/roulette/" style={{ color: 'var(--Color-fill-brand-color, #FF6F0F)' }}>
            Marble Roulette
          </a>
          과 같은 방식의 Box2D 물리 시뮬레이션입니다. 아래에서 맵·속도·커스텀 트랙을 바꿀 수 있습니다.
        </p>
        {physicsError && (
          <p style={{ fontSize: '0.875rem', color: '#c62828', marginBottom: '1rem' }}>
            물리 엔진을 불러오지 못했습니다: {physicsError}
          </p>
        )}
        {!physicsReady && !physicsError && (
          <p style={{ fontSize: '0.875rem', marginBottom: '1rem', color: 'var(--Color-text-normal, #666)' }}>
            물리 엔진 로딩 중…
          </p>
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'flex-end',
            marginBottom: '1rem',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8125rem' }}>
            맵 소스
            <select
              value={mapSource}
              onChange={e => setMapSource(e.target.value as 'builtin' | 'custom')}
              disabled={isRunning}
              style={{ padding: '8px 10px', borderRadius: '8px', minWidth: '160px' }}
            >
              <option value="builtin">내장 맵</option>
              <option value="custom">저장된 커스텀 맵</option>
            </select>
          </label>
          {mapSource === 'builtin' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8125rem' }}>
              트랙 선택
              <select
                value={builtinMapIndex}
                onChange={e => setBuiltinMapIndex(Number(e.target.value))}
                disabled={isRunning}
                style={{ padding: '8px 10px', borderRadius: '8px', minWidth: '200px' }}
              >
                {stages.map((s, i) => (
                  <option key={`${i}-${s.title}`} value={i}>
                    {s.title}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span style={{ fontSize: '0.8125rem', color: '#666', paddingBottom: '8px' }}>
              {customStage?.title ?? '저장된 맵 없음 — 편집기에서 저장하세요'}
            </span>
          )}
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8125rem' }}>
            속도 ×{speed.toFixed(2)}
            <input
              type="range"
              min={0.25}
              max={2.5}
              step={0.05}
              value={speed}
              onChange={e => setSpeed(Number(e.target.value))}
              disabled={!physicsReady}
              style={{ width: '140px' }}
            />
          </label>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <Button
            variant="outline"
            size="medium"
            type="button"
            onClick={() => setShowMapEditor(v => !v)}
            disabled={!physicsReady || isRunning}
          >
            {showMapEditor ? '맵 편집기 닫기' : '맵 편집기'}
          </Button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button
            variant="primary"
            size="medium"
            onClick={winners.length === 0 ? startRound : drawNext}
            disabled={!canStart}
          >
            {isRunning ? '진행 중…' : winners.length === 0 ? '추첨 시작' : '다음 추첨'}
          </Button>
          {winners.length > 0 && (
            <Button variant="secondary" size="medium" onClick={reset} disabled={isRunning}>
              처음부터
            </Button>
          )}
        </div>
      </div>

      <div
        style={{
          width: '100%',
          maxWidth: '720px',
          margin: '0 auto 2rem',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid var(--Color-line-normal, #e0e0e0)',
          background: '#0d0d0d',
        }}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-label="구슬 레이스 시뮬레이션"
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            verticalAlign: 'bottom',
          }}
        />
      </div>

      {showMapEditor && (
        <MarbleMapEditor
          initialStage={effectiveStage}
          onSave={handleEditorSave}
          onClose={() => setShowMapEditor(false)}
        />
      )}

      {selectedStudent && (
        <div
          style={{
            padding: '1.5rem',
            backgroundColor: '#FFF4E6',
            borderRadius: '0.5rem',
            border: '2px solid #FFA726',
            marginBottom: '1.5rem',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🎉</div>
          <div
            style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#FF8F00',
              marginBottom: '0.25rem',
            }}
          >
            {selectedStudent.name}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#666' }}>당첨을 축하합니다!</div>
        </div>
      )}

      {showRanking && winners.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3
            style={{
              fontSize: '1rem',
              fontWeight: 600,
              marginBottom: '1rem',
              color: 'var(--Color-text-normal, #161616)',
            }}
          >
            추첨 결과 ({winners.length}명)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {winners.map((student, index) => (
              <div
                key={student.uid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '0.75rem',
                  backgroundColor:
                    index === 0 ? '#FFF4E6' : index === 1 ? '#F0F0F0' : index === 2 ? '#FFF8E1' : '#F8F8F8',
                  borderRadius: '0.375rem',
                  border: index === 0 ? '2px solid #FFA726' : '1px solid #E0E0E0',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor:
                      index === 0 ? '#FFA726' : index === 1 ? '#9E9E9E' : index === 2 ? '#FFC107' : '#E0E0E0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {index + 1}
                </div>
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#161616' }}>{student.name}</span>
                {index === 0 && (
                  <span
                    style={{
                      fontSize: '0.75rem',
                      color: '#FFA726',
                      fontWeight: 600,
                      marginLeft: 'auto',
                    }}
                  >
                    🏆 당첨!
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default MarbleLottery;
